import { types, flow } from 'mobx-state-tree';
import Subscription from './Subscription';
import Delivery from './Delivery';
import API from './../../api/webhooks';
import requsetErrorMessage from '../../helpers/requsetErrorMessage';
import clearEmptyFields from '../../helpers/clearEmptyFields';

const Webhooks = types
    .model('Webhooks', {
        subscriptions: types.array(Subscription),
        selectedSubscription: types.maybe(types.reference(Subscription)),
        deliveries: types.array(Delivery),
        deliveriesTotal: types.optional(types.number, 0),
        deliveriesPage: types.optional(types.number, 1),
        deliveriesLimit: types.optional(types.number, 50),
        deliveriesFilters: types.frozen({}),
        error: types.union(
            types.optional(types.string, ''),
            types.optional(types.array(types.string), []),
        ),
        dataLoaded: types.optional(types.boolean, false),
    })
    .actions((self) => ({
        getSubscriptions: flow(function* getSubscriptions() {
            self.error = '';
            self.subscriptions = [];
            try {
                const res = yield API.getSubscriptions();
                self.dataLoaded = true;
                self.subscriptions = res.data;
            } catch (error) {
                self.dataLoaded = true;
                self.error = requsetErrorMessage(error && error.message ? error.message : error);
            }
        }),
        selectSubscription(id) {
            self.selectedSubscription = id;
        },
        getSubscription: flow(function* getSubscription(id) {
            self.error = '';
            try {
                const res = yield API.getSubscription(id);
                self.dataLoaded = true;
                self.subscriptions = [];
                self.subscriptions.push(res.data);
                self.selectedSubscription = res.data.id;
            } catch (error) {
                self.dataLoaded = true;
                self.error = requsetErrorMessage(error && error.message ? error.message : error);
            }
        }),
        updateSubscription: flow(function* updateSubscription(edited) {
            try {
                const res = yield API.updateSubscription(
                    self.selectedSubscription.id,
                    clearEmptyFields({ ...edited }),
                );
                self.selectedSubscription.applyEdit(res.data);
            } catch (error) {
                throw error;
            }
        }),
        createSubscription: flow(function* createSubscription(data) {
            // Returns the secret (visible only once). Caller must show it.
            const res = yield API.createSubscription(clearEmptyFields({ ...data }));
            const { secret, ...rest } = res.data;
            self.subscriptions.push(rest);
            return secret;
        }),
        rotateSecret: flow(function* rotateSecret(id) {
            const res = yield API.rotateSecret(id);
            return res.data.secret;
        }),
        deleteSubscription: flow(function* deleteSubscription(id) {
            try {
                yield API.deleteSubscription(id);
                if (self.selectedSubscription && self.selectedSubscription.id === id) {
                    self.selectedSubscription = undefined;
                }
                self.subscriptions = self.subscriptions.filter((s) => s.id !== id);
            } catch (error) {
                self.error = requsetErrorMessage(error && error.message ? error.message : error);
            }
        }),
        sendTest: flow(function* sendTest(id) {
            const res = yield API.sendTest(id);
            return res.data;
        }),
        getDeliveries: flow(function* getDeliveries(filters = {}) {
            self.error = '';
            self.deliveriesFilters = filters;
            try {
                const res = yield API.getDeliveries({
                    params: {
                        ...filters,
                        page: self.deliveriesPage,
                        limit: self.deliveriesLimit,
                    },
                });
                self.dataLoaded = true;
                self.deliveries = res.data.items;
                self.deliveriesTotal = res.data.total;
            } catch (error) {
                self.dataLoaded = true;
                self.error = requsetErrorMessage(error && error.message ? error.message : error);
            }
        }),
        retryDelivery: flow(function* retryDelivery(id) {
            try {
                yield API.retryDelivery(id);
                yield self.getDeliveries(self.deliveriesFilters);
            } catch (error) {
                self.error = requsetErrorMessage(error && error.message ? error.message : error);
            }
        }),
        setDeliveriesPage(page) {
            self.deliveriesPage = page;
        },
        setDataLoaded() {
            self.dataLoaded = true;
        },
    }))
    .views((self) => ({
        get deliveriesTotalPages() {
            return Math.max(1, Math.ceil(self.deliveriesTotal / self.deliveriesLimit));
        },
    }));

export default Webhooks;
